import React from "react";
import { FaCompass, FaHandshake, FaHotel, FaCogs, FaHeart, FaArrowRight, FaShieldAlt } from "react-icons/fa";
import { Link } from "react-router-dom";
import "../css/styles.css";

const whyUs = [
  {
    icon: <FaCompass />,
    title: "Tailor-Made Journeys",
    desc: "No two travelers are the same, and neither are our safaris. Every journey we design is carefully tailored around your interests, travel style, and pace, ensuring an experience that feels uniquely yours.",
  },
  {
    icon: <FaHandshake />,
    title: "Local Knowledge & Trusted Partners",
    desc: "Our deep connection to East Africa allows us to recommend exceptional safari camps, boutique lodges, and meaningful experiences that truly capture the spirit of the region.",
  },
  {
    icon: <FaHotel />,
    title: "Handpicked Stays",
    desc: "From intimate tented camps in the Serengeti to elegant lodges overlooking the Masai Mara and tranquil beach resorts along the Indian Ocean, we carefully select accommodations that combine comfort, location, and authenticity.",
  },
  {
    icon: <FaCogs />,
    title: "Seamless Planning",
    desc: "From the moment you begin planning until you return home, we take care of every detail. Our goal is to make your journey effortless, allowing you to focus entirely on the adventure.",
  },
  {
    icon: <FaHeart />,
    title: "Thoughtful & Personal Service",
    desc: "As a passionate and dedicated team, we give each trip our full attention. We believe the most memorable journeys are created with care, understanding, and a genuine love for what we do.",
  },
];

const team = [
  { name: "Ms. Helen Nyaguthii", role: "Co-Founder" },
  { name: "Ms. Charlotte Murugi", role: "Co-Founder | Operations Manager | Travel Designer" },
  { name: "Ms. Lucy Wangari", role: "Safari Designer" },
  { name: "Ms. Jane Muthoni", role: "Meet & Assist" },
];

const About = () => {
  return (
    <div className="abt-page">
      {/* ===== HERO ===== */}
      <section className="abt-hero">
        <img
          src="https://res.cloudinary.com/dn39bkkiq/image/upload/q_auto,f_auto,w_1920/v1776344741/About_Us_Hero_image_rekvjw.jpg"
          alt="About Nyatwende"
          className="abt-hero-bg"
        />
        <div className="abt-hero-overlay" />
        <div className="abt-hero-content">
          <span className="dest-hero-eyebrow">Our Story</span>
          <h1 className="abt-hero-title">About Us</h1>
          <p className="abt-hero-sub">
            Born from a deep love for East Africa and the extraordinary experiences this land offers.
          </p>
        </div>
      </section>

      {/* ===== STORY ===== */}
      <section className="abt-story">
        <div className="abt-story-inner">
          <span className="section-tag centered">Who We Are</span>
          <h2 className="section-heading abt-story-heading">Our Journey</h2>
          <div className="abt-story-text">
            <p>
              At the heart of every unforgettable journey is a story &mdash; and ours begins with a deep love for East Africa and the extraordinary experiences this land offers.
            </p>
            <p>
              Our company was born from a simple belief: travel should be personal, meaningful, and thoughtfully crafted. Kenya and Tanzania are more than destinations to us; they are places of breathtaking wilderness, vibrant cultures, and timeless moments that deserve to be experienced in a way that feels authentic and unhurried.
            </p>
            <p>
              From the golden savannahs of the Masai Mara to the endless plains of the Serengeti, from the dramatic Ngorongoro Crater to the turquoise shores of Zanzibar, we design journeys that reveal the true spirit of East Africa. Every itinerary we create is tailored with care, ensuring that each traveler experiences the landscapes, wildlife, and cultures of this region in a way that feels unique to them.
            </p>
            <p>
              As a young and passionate company, we take pride in giving every journey our full attention. We carefully select the lodges we recommend, collaborate with trusted local partners, and remain personally involved throughout the planning process to ensure every detail comes together seamlessly.
            </p>
            <p>
              For us, this is more than travel planning &mdash; it is about helping our guests experience the wonder of Africa in a way that stays with them long after the journey ends.
            </p>
            <p className="abt-story-invite">We invite you to discover East Africa with us.</p>
          </div>
        </div>
      </section>

      {/* ===== WHY TRAVEL WITH US ===== */}
      <section className="abt-why">
        <div className="abt-why-inner">
          <span className="section-tag centered abt-why-tag">The Difference</span>
          <h2 className="section-heading abt-why-heading">Why Travel With Us</h2>

          <div className="abt-why-grid">
            {whyUs.map((item, i) => (
              <div className="abt-why-card" key={i}>
                <span className="abt-why-card-icon">{item.icon}</span>
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== TEAM ===== */}
      <section className="abt-team">
        <div className="abt-team-inner">
          <span className="section-tag centered">The People</span>
          <h2 className="section-heading abt-team-heading">Meet Our Team</h2>
          <p className="abt-team-sub">The passionate people behind every unforgettable safari</p>

          <div className="abt-team-grid">
            {team.map((member, i) => (
              <div className="abt-team-card" key={i}>
                <div className="abt-team-avatar">
                  <span>{member.name.split(" ").map(w => w[0]).slice(0, 2).join("")}</span>
                </div>
                <h3 className="abt-team-name">{member.name}</h3>
                <p className="abt-team-role">{member.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="cta-section cta-clean">
        <div className="cta-content">
          <span className="cta-eyebrow">Your Adventure Awaits</span>
          <h2>Design Your Trip</h2>
          <p>
            Ready to experience the wonder of East Africa? Let us craft a journey
            as unique as you are.
          </p>
          <div className="hero-buttons" style={{ justifyContent: "center" }}>
            <Link to="/tours" className="cta-button">
              Start Planning <FaArrowRight />
            </Link>
          </div>

          {/* Trust Badges */}
          <div className="abt-trust-badges">
            <div className="abt-trust-badge">
              <FaShieldAlt className="abt-trust-icon" />
              <span>TRA Licensed</span>
            </div>
            <div className="abt-trust-badge">
              <FaShieldAlt className="abt-trust-icon tripadvisor" />
              <span>Tripadvisor Rated</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;